import { useState, useMemo } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { formatDateTime } from "@/lib/format";
import { toast } from "sonner";
import { Inbox, CheckCircle2, Send, MessageSquare } from "lucide-react";

const qName = (q) => q.customer_name || q.name || q.from_name || q.user_name || q.raised_by || "—";
const qSubject = (q) => q.subject || q.title || q.topic || q.loan_type || "Query";
const qBody = (q) => q.message || q.body || q.text || q.description || q.comment || "";
const qThread = (q) => q.messages || q.replies || q.thread || q.comments || q.conversation || [];
const mBody = (m) => m.comment || m.message || m.body || m.text || "";
const mWho = (m) => m.user_name || m.from_name || m.author || m.sender || (m.is_staff ? "Staff" : "Customer");
const mAt = (m) => m.at || m.created_at || m.timestamp || m.time;

function QueryDetail({ queryId, open, onOpenChange }) {
  const qc = useQueryClient();
  const [reply, setReply] = useState("");
  const { data: q, isLoading } = useQuery({ queryKey: ["query", queryId], queryFn: async () => (await api.get(`/queries/${queryId}`)).data, enabled: !!queryId && open });

  const invalidate = () => { qc.invalidateQueries({ queryKey: ["query", queryId] }); qc.invalidateQueries({ queryKey: ["queries"] }); qc.invalidateQueries({ queryKey: ["queries-count"] }); };
  const sendReply = useMutation({
    mutationFn: async (resolve) => (await api.post(`/queries/${queryId}/reply`, { comment: reply, resolve: !!resolve })).data,
    onSuccess: () => { setReply(""); invalidate(); toast.success("Reply sent"); },
    onError: (e) => toast.error(apiError(e)),
  });
  const resolve = useMutation({
    mutationFn: async () => (await api.post(`/queries/${queryId}/resolve`, {})).data,
    onSuccess: () => { invalidate(); toast.success("Query resolved"); onOpenChange(false); },
    onError: (e) => toast.error(apiError(e)),
  });

  const thread = q ? qThread(q) : [];
  const resolved = q?.status === "resolved";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl bg-white dark:bg-slate-900" data-testid="query-detail-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">{q ? qSubject(q) : "Query"}{resolved && <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300">Resolved</Badge>}</DialogTitle>
          <DialogDescription>{q ? `${qName(q)}${q.phone ? ` · ${q.phone}` : ""}` : "Loading…"}</DialogDescription>
        </DialogHeader>
        {isLoading && <Skeleton className="h-40 w-full" />}
        {q && (
          <div className="space-y-4">
            {qBody(q) && <div className="text-sm bg-slate-50 dark:bg-slate-800/50 rounded-lg p-3 text-slate-700 dark:text-slate-200">{qBody(q)}</div>}
            <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="query-thread">
              {thread.length === 0 && <p className="text-sm text-slate-400">No replies yet.</p>}
              {thread.map((m, i) => (
                <div key={i} className="text-sm border border-slate-100 dark:border-slate-800 rounded-lg p-2.5">
                  <div className="flex justify-between"><span className="font-medium text-slate-800 dark:text-slate-200">{mWho(m)}</span><span className="text-xs text-slate-400 font-num">{mAt(m) ? formatDateTime(mAt(m)) : ""}</span></div>
                  <div className="text-slate-600 dark:text-slate-300 mt-0.5">{mBody(m)}</div>
                </div>
              ))}
            </div>
            {!resolved && (
              <div className="space-y-2 border-t border-slate-100 dark:border-slate-800 pt-3">
                <Textarea value={reply} onChange={(e) => setReply(e.target.value)} placeholder="Type your reply…" data-testid="query-reply-input" />
                <div className="flex items-center justify-between">
                  <Button variant="outline" size="sm" onClick={() => resolve.mutate()} disabled={resolve.isPending} data-testid="query-resolve-button"><CheckCircle2 className="h-4 w-4 mr-1" />Mark resolved</Button>
                  <div className="flex gap-2">
                    <Button size="sm" variant="secondary" disabled={!reply.trim() || sendReply.isPending} onClick={() => sendReply.mutate(true)} data-testid="query-reply-resolve-button">Reply & resolve</Button>
                    <Button size="sm" className="bg-blue-700 hover:bg-blue-800 text-white" disabled={!reply.trim() || sendReply.isPending} onClick={() => sendReply.mutate(false)} data-testid="query-reply-button"><Send className="h-4 w-4 mr-1" />Send</Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default function Queries() {
  const [scope, setScope] = useState("inbox");
  const [status, setStatus] = useState("open");
  const [active, setActive] = useState(null);

  const { data: counts } = useQuery({ queryKey: ["queries-count"], queryFn: async () => (await api.get("/queries/count")).data });
  const { data: queries = [], isLoading } = useQuery({
    queryKey: ["queries", scope, status],
    queryFn: async () => {
      const params = { scope };
      if (status !== "all") params.status = status;
      return (await api.get("/queries", { params })).data;
    },
  });

  const list = useMemo(() => (Array.isArray(queries) ? queries : queries?.items || []), [queries]);

  return (
    <div data-testid="queries-page">
      <PageHeader title="Queries" subtitle={`${counts?.total_open ?? 0} open · reply to customer and team queries, synced with the mobile app.`} />

      <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
        <Tabs value={scope} onValueChange={setScope}>
          <TabsList>
            <TabsTrigger value="inbox" data-testid="queries-tab-inbox"><Inbox className="h-4 w-4 mr-1.5" />Inbox{counts?.inbox_open ? ` (${counts.inbox_open})` : ""}</TabsTrigger>
            <TabsTrigger value="sent" data-testid="queries-tab-sent"><Send className="h-4 w-4 mr-1.5" />Sent</TabsTrigger>
            <TabsTrigger value="all" data-testid="queries-tab-all">All</TabsTrigger>
          </TabsList>
        </Tabs>
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="w-40 bg-white dark:bg-slate-950" data-testid="queries-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="open">Open</SelectItem><SelectItem value="resolved">Resolved</SelectItem><SelectItem value="all">All</SelectItem></SelectContent>
        </Select>
      </div>

      <div className="space-y-2" data-testid="queries-list">
        {isLoading && Array.from({ length: 5 }).map((_, i) => <Skeleton key={i} className="h-16 w-full rounded-xl" />)}
        {!isLoading && list.length === 0 && <Card className="p-10 text-center text-slate-400 border-slate-200 dark:border-slate-800"><MessageSquare className="h-8 w-8 mx-auto mb-2 opacity-40" />No queries here.</Card>}
        {!isLoading && list.map((q) => {
          const resolved = q.status === "resolved";
          return (
            <Card key={q.id} data-testid={`query-row-${q.id}`} onClick={() => setActive(q.id)} className="p-4 flex items-center justify-between gap-4 cursor-pointer border-slate-200 dark:border-slate-800 hover:border-blue-300 dark:hover:border-blue-700 hover:shadow-sm transition-all">
              <div className="min-w-0">
                <div className="flex items-center gap-2"><span className="font-medium text-slate-900 dark:text-slate-100 truncate">{qSubject(q)}</span>{resolved ? <Badge className="border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-950/50 dark:text-emerald-300 text-[10px]">Resolved</Badge> : <Badge className="border-0 bg-amber-100 text-amber-700 dark:bg-amber-950/50 dark:text-amber-300 text-[10px]">Open</Badge>}</div>
                <div className="text-sm text-slate-500 truncate">{qName(q)} · {qBody(q) || "—"}</div>
              </div>
              <div className="text-xs text-slate-400 font-num shrink-0">{q.created_at ? formatDateTime(q.created_at) : ""}</div>
            </Card>
          );
        })}
      </div>

      {active && <QueryDetail queryId={active} open={!!active} onOpenChange={(o) => !o && setActive(null)} />}
    </div>
  );
}
